import React, { useState, useRef } from 'react';

import ReactCrop from 'react-image-crop';

import {
  canvasPreview,
  centerAspectCrop,
  useDebounceEffect,
} from './ImageUploadToolHelpers';
import Form from '../../Form/components/Form';
import Field from '../../Field/components/Field';
import FileInput from '../../FileInput/components/FileInput';
import FormActions from '../../FormActions/components/FormActions';
import Button from '../../Button/components/Button';
import SliderField from '../../SliderField/components/SliderField';

import 'react-image-crop/dist/ReactCrop.css';

import styles from './ImageUploadTool.module.scss';

// This component is a wrapper around ReactCrop from react-image-crop
// https://www.npmjs.com/package/react-image-crop
const ImageUploadTool = ({
  useProviderState,
  updateProfilePictureBase64StringAndFileName,
  onSaveClick,
  onCancelClick,
}) => {
  const providerState = useProviderState();

  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [scale, setScale] = useState(1);
  const [aspect, setAspect] = useState(1);

  const currentImageSrc = providerState.userProfilePictureForm?.profilePictureBase64String
    ? `data:image/gif;base64,${providerState.userProfilePictureForm.profilePictureBase64String}`
    : providerState.userProfilePictureForm?.profilePicturePath || null;

  function onSelectFile(e) {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined)
      const reader = new FileReader()
      reader.addEventListener(
        'load',
        () => {
          const base64String = reader.result
            ?.replace("data", "")
            ?.replace(/^.+,/, "") ||
            '';

          updateProfilePictureBase64StringAndFileName(
            base64String,
            `${providerState?.user?.firstName || 'user'}_${providerState?.user?.lastName || 'profile'}_profile_picture.jpg`
          )
        }
      )
      reader.readAsDataURL(e.target.files[0])
    }
  };

  function onImageLoad(e) {
    if (aspect) {
      const { width, height } = e.currentTarget
      const initialCrop = centerAspectCrop(width, height, aspect)
      setCrop(initialCrop)
      setCompletedCrop(initialCrop)
    }
  };

  async function onClickSavePreviewedImage() {
    const previewCanvas = previewCanvasRef.current

    if (!previewCanvas) {
      throw new Error('Crop canvas does not exist')
    }

    const base64String = previewCanvas.toDataURL('image/jpeg', 1.0)
      ?.replace("data", "")
      ?.replace(/^.+,/, "") ||
      '';

    onSaveClick(base64String);
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
        )
      }
    },
    100,
    [completedCrop, scale],
  );

  return (
    <Form>
      {currentImageSrc && (
        <div className={styles.imageContainer}>
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            minHeight={100}
          >
            <img
              id={styles.selectedProfilePicture}
              ref={imgRef}
              alt="Crop me"
              src={currentImageSrc}
              style={{ transform: `scale(${scale})` }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        </div>
      )}
      <Field label="Upload And Crop">
        <FileInput
          accept="image/*"
          onChange={onSelectFile}
        />
      </Field>
      <SliderField
        label="Scale"
        min={0.1}
        max={3}
        step={0.1}
        value={scale}
        unit="x"
        ticks={[0.5, 1, 2, 3]}
        disabled={!currentImageSrc}
        onChange={(e) => setScale(Number(e.target.value))}
      />
      <h3 className={styles.title}>Profile Preview</h3>
      {!!completedCrop && (
        <div className={styles.previewContainer}>
          <canvas
            ref={previewCanvasRef}
            id={styles.profilePicturePreview}
          />
        </div>
      )}
      <FormActions>
        <Button
          variant="secondary"
          onClick={onCancelClick}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onClickSavePreviewedImage}
        >
          Save Previewed Image
        </Button>
      </FormActions>
    </Form>
  );
};

export default ImageUploadTool;
